import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { canPerformAction } from '../utils/permissionUtils';
import CalendarEntryDialog from './CalendarEntryDialog';
import CalendarViewDialog from './CalendarViewDialog';
import CalendarDayDialog from './CalendarDayDialog';
import CollapsibleSection from './CollapsibleSection';
import '../styles/calendar.css';

const Calendar = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  
  // State for calendar
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarEntries, setCalendarEntries] = useState([]);
  const [rehearsalDates, setRehearsalDates] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Dialog states
  const [showEntryDialog, setShowEntryDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showDayDialog, setShowDayDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  
  // Permission check
  const canManageCalendar = user && (canPerformAction(user, 'manage-calendar') || user.role === 'super-admin');
  
  
  // Debug permission
  useEffect(() => {
    if (user) {
      console.log('Calendar permissions check:', {
        user: user.email,
        role: user.role,
        permissions: user.permissions,
        canManageCalendar,
        hasDirectPermission: canPerformAction(user, 'manage-calendar')
      });
    }
  }, [user, canManageCalendar]);
  
  // Get calendar data for current month
  useEffect(() => {
    loadCalendarData();
  }, [currentDate]);

  // Handle URL parameters for editing calendar entries
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const editId = urlParams.get('edit');
    const editDate = urlParams.get('date');
    
    if (editId && editDate && canManageCalendar) {
      // Load the specific entry for editing
      loadEntryForEditing(editId, editDate);
    }
  }, [canManageCalendar]);
  
  const loadCalendarData = async () => {
    setLoading(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      
      // Get first and last day of month with some padding
      const startDate = new Date(year, month - 1, 1).toISOString().split('T')[0];
      const endDate = new Date(year, month + 2, 0).toISOString().split('T')[0];
      
      // Load calendar entries
      const entriesResponse = await fetch(`/api/calendar?operation=getEntries&startDate=${startDate}&endDate=${endDate}`);
      if (entriesResponse.ok) {
        const entries = await entriesResponse.json();
        console.log('Calendar entries loaded:', entries);
        setCalendarEntries(entries);
      } else {
        console.error('Failed to load calendar entries:', entriesResponse.status);
      }
      
      // Load rehearsal dates
      const rehearsalsResponse = await fetch(`/api/calendar?operation=getRehearsalDates&startDate=${startDate}&endDate=${endDate}`);
      if (rehearsalsResponse.ok) {
        const rehearsals = await rehearsalsResponse.json();
        console.log('🎵 Rehearsal dates received by client:', rehearsals.map(r => ({ 
          date: r.date, 
          dayOfWeek: new Date(r.date + 'T00:00:00').getDay(),
          dayName: ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][new Date(r.date + 'T00:00:00').getDay()]
        })));
        setRehearsalDates(rehearsals);
      }
    } catch (error) {
      console.error('Failed to load calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load a specific entry for editing from URL parameters
  const loadEntryForEditing = async (entryId, entryDate) => {
    try {
      const response = await fetch(`/api/calendar?operation=getEntry&id=${entryId}`);
      if (response.ok) {
        const entry = await response.json();
        
        // Set the calendar to the correct month/year for the entry
        const entryDateObj = new Date(entryDate);
        setCurrentDate(entryDateObj);
        
        // Set up the entry for editing
        setSelectedDate(entryDate);
        setEditingEntry(entry);
        setShowEntryDialog(true);
        
        // Scroll to calendar section after a short delay
        setTimeout(() => {
          const calendarSection = document.querySelector('.calendar-section');
          if (calendarSection) {
            calendarSection.scrollIntoView({ behavior: 'smooth' });
          }
        }, 500);
        
        // Clear URL parameters after opening the dialog
        const url = new URL(window.location);
        url.searchParams.delete('edit');
        url.searchParams.delete('date');
        window.history.replaceState({}, '', url.toString());
      } else {
        console.error('Failed to load calendar entry for editing:', response.status);
        alert('Failed to load calendar entry for editing');
      }
    } catch (error) {
      console.error('Failed to load calendar entry for editing:', error);
      alert('Failed to load calendar entry for editing');
    }
  };
  
  // Navigate months
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };
  
  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay()); // Start from Sunday
    
    const days = [];
    const today = new Date();
    const currentMonth = month;
    
    for (let i = 0; i < 42; i++) { // 6 weeks * 7 days
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      
      const dateStr = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
      const isCurrentMonth = day.getMonth() === currentMonth;
      const isToday = day.toDateString() === today.toDateString();
      const isPast = day < today && !isToday;
      
      
      // Find entries and rehearsals for this date
      const dayEntries = calendarEntries.filter(entry => {
        const entryDate = entry.date.split('T')[0]; // Convert '2025-06-29T00:00:00.000Z' to '2025-06-29'
        return entryDate === dateStr;
      });
      const dayRehearsals = rehearsalDates.filter(rehearsal => rehearsal.date === dateStr);
      
      // Debug logging for rehearsal matching
      // if (dayRehearsals.length > 0) {
      //   console.log(`🎵 REHEARSAL MATCH: ${dateStr} (${['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][day.getDay()]}) has ${dayRehearsals.length} rehearsals`);
      // }
      
      const dayData = {
        date: day,
        dateStr,
        dayNumber: day.getDate(),
        isCurrentMonth,
        isToday,
        isPast,
        entries: dayEntries,
        rehearsals: dayRehearsals,
        hasContent: dayEntries.length > 0 || dayRehearsals.length > 0
      };
      
      
      days.push(dayData);
    }
    
    return days;
  };
  
  // Handle day click - show appropriate dialog based on content
  const handleDayClick = (day) => {
    const totalItems = day.rehearsals.length + day.entries.length;
    
    if (totalItems === 0) {
      // No items, do nothing
      return;
    }
    
    // Always show the day dialog on mobile for better UX
    const isMobile = window.innerWidth <= 768;
    
    if (isMobile || totalItems > 1) {
      // Show day dialog for multiple items or on mobile
      setSelectedDay(day);
      setShowDayDialog(true);
      return;
    }
    
    // Desktop with single item - show directly (existing behavior)
    const rehearsalNotesEntries = day.entries.filter(entry => entry.entry_type === 'rehearsal_notes');
    const otherEntries = day.entries.filter(entry => entry.entry_type !== 'rehearsal_notes');
    
    if (day.rehearsals.length === 1 && otherEntries.length === 0) {
      // Single rehearsal (with or without notes)
      const rehearsal = day.rehearsals[0];
      const rehearsalNotes = rehearsalNotesEntries[0];
        
        const rehearsalItem = {
          id: `rehearsal-${rehearsal.date}-${rehearsal.time}`,
          entry_type: 'rehearsal_info',
          title: `Rehearsal - ${rehearsal.time}`,
          date: day.dateStr,
          content: `Scheduled rehearsal at ${rehearsal.time}${rehearsal.location ? ` at ${rehearsal.location}` : ''}`,
          created_at: new Date().toISOString(),
          audio_clips: rehearsalNotes?.audio_clips || [],
          hasNotes: !!rehearsalNotes,
          associatedNotes: rehearsalNotes,
          isRehearsalInfo: true,
          rehearsal: rehearsal
        };
        
        setSelectedEntry(rehearsalItem);
        setShowViewDialog(true);
    } else {
      // Single regular entry (not rehearsal)
      setSelectedEntry(otherEntries[0] || rehearsalNotesEntries[0]);
      setShowViewDialog(true);
    }
  };
  
  // Handle selection from day dialog
  const handleDayDialogSelection = (item) => {
    setShowDayDialog(false);
    setSelectedEntry(item);
    setShowViewDialog(true);
  };
  
  // Handle add entry button click
  const handleAddEntryClick = (e, dateStr) => {
    e.stopPropagation(); // Prevent day click
    if (!canManageCalendar) return;
    
    setSelectedDate(dateStr);
    setEditingEntry(null);
    setShowEntryDialog(true);
  };
  
  // Handle add entry
  const handleAddEntry = (dateStr = null) => {
    if (!canManageCalendar) return;
    
    setSelectedDate(dateStr || new Date().toISOString().split('T')[0]);
    setEditingEntry(null);
    setShowEntryDialog(true);
  };
  
  // Handle edit entry
  const handleEditEntry = (entry) => {
    if (!canManageCalendar) return;
    
    // Close view dialog first
    setShowViewDialog(false);
    
    // Special case: if editing a rehearsal_info entry, redirect to rehearsal management
    if (entry.entry_type === 'rehearsal_info') {
      // If there are associated notes, edit those instead
      if (entry.associatedNotes) {
        setSelectedDate(entry.associatedNotes.date.split('T')[0]);
        setEditingEntry(entry.associatedNotes);
        setShowEntryDialog(true);
        return;
      }
      
      // Otherwise, redirect to rehearsal management
      // You could implement navigation to rehearsal management here
      alert('To edit rehearsal details, please use the Rehearsal Management section.');
      return;
    }
    
    // Regular entry editing
    setSelectedDate(entry.date.split('T')[0]); // Convert to date string format
    setEditingEntry(entry);
    setShowEntryDialog(true);
  };
  
  // Handle delete entry
  const handleDeleteEntry = async (entry) => {
    if (!canManageCalendar) return;
    
    // Special handling for rehearsal_info entries with associated notes
    const isRehearsalWithNotes = entry.entry_type === 'rehearsal_info' && entry.associatedNotes;
    const targetEntry = isRehearsalWithNotes ? entry.associatedNotes : entry;
    const confirmMessage = isRehearsalWithNotes 
      ? `Are you sure you want to delete the rehearsal notes "${targetEntry.title}"?`
      : `Are you sure you want to delete "${entry.title}"?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      const response = await fetch(`/api/calendar?operation=deleteEntry&id=${targetEntry.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          userRole: user.role
        })
      });
      
      if (response.ok) {
        setShowViewDialog(false);
        loadCalendarData(); // Refresh data
      } else {
        const error = await response.json();
        console.error('Server error response:', error);
        throw new Error(error.details || error.error || 'Failed to delete entry');
      }
    } catch (error) {
      console.error('Failed to delete calendar entry:', error);
      alert(`Failed to delete entry: ${error.message}`);
    }
  };
  
  // Handle entry saved
  const handleEntrySaved = () => {
    setShowEntryDialog(false);
    setShowViewDialog(false);
    loadCalendarData(); // Refresh data
  };
  
  // Handle view all entries for a day
  const handleViewAllEntries = (day) => {
    if (day.entries.length > 1) {
      // TODO: Implement multi-entry view dialog
      setSelectedEntry(day.entries[0]);
      setShowViewDialog(true);
    } else if (day.entries.length === 1) {
      setSelectedEntry(day.entries[0]);
      setShowViewDialog(true);
    }
  };
  
  // Format month/year display
  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };
  
  const calendarDays = generateCalendarDays();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  return (
    <CollapsibleSection 
      title="Choir Calendar"
      icon="🗓️"
      defaultExpanded={true}
      className="calendar-section"
    >
      <div className="calendar-container">
        <div className="calendar-header">
          <div className="calendar-navigation">
            <button 
              className="nav-button" 
              onClick={() => navigateMonth(-1)}
              disabled={loading}
            >
              ‹ Previous
            </button>
            
            <span className="month-year">
              {formatMonthYear(currentDate)}
            </span>
            
            <button 
              className="nav-button" 
              onClick={() => navigateMonth(1)}
              disabled={loading}
            >
              Next ›
            </button>
          </div>
        </div>
        
        <div className="calendar-grid">
          {/* Week day headers */}
          <div className="calendar-weekdays">
            {weekDays.map(day => (
              <div key={day} className="weekday-header">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar days */}
          <div className="calendar-days">
            {calendarDays.map((day, index) => (
              <div
                key={index}
                className={`calendar-day ${
                  !day.isCurrentMonth ? 'other-month' : ''
                } ${
                  day.isToday ? 'today' : ''
                } ${
                  day.isPast ? 'past' : ''
                } ${
                  day.hasContent ? 'has-content' : ''
                } ${
                  day.rehearsals.length > 0 ? 'has-rehearsal' : ''
                }`}
                onClick={() => handleDayClick(day)}
              >
                <div className="day-header">
                  <span className="day-number">{day.dayNumber}</span>
                  {canManageCalendar && (
                    <button 
                      className="day-add-button"
                      onClick={(e) => handleAddEntryClick(e, day.dateStr)}
                      title={`Add calendar entry`}
                      style={{
                        background: '#4a90e2',
                        color: 'white',
                        fontWeight: 'bold',
                        opacity: '1',
                        transform: 'scale(1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '20px',
                        height: '20px',
                        border: 'none',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        fontSize: '12px',
                        zIndex: 100,
                        position: 'relative',
                        visibility: 'visible'
                      }}
                    >
                      +
                    </button>
                  )}
                </div>
                
                {/* Mobile: Simple indicator for days with items */}
                <div className="day-indicators mobile-only">
                  <div className="mobile-icons-row">
                    {day.rehearsals.length > 0 && (
                      <span className="mobile-icon rehearsal-icon" title="Rehearsal">🎵</span>
                    )}
                    {day.entries.length > 0 && (
                      <span className="mobile-icon entries-icon" title={`${day.entries.length} item${day.entries.length > 1 ? 's' : ''}`}>📋</span>
                    )}
                  </div>
                </div>
                
                {/* Desktop: Detailed indicators */}
                <div className="day-indicators desktop-only">
                  {day.rehearsals.map((rehearsal, i) => (
                    <div key={i} className="rehearsal-indicator" title={`Rehearsal at ${rehearsal.time}`}>
                      <span className="indicator-icon">🎵</span>
                      <span className="indicator-text">{rehearsal.time}</span>
                    </div>
                  ))}
                  {day.entries.map((entry, i) => (
                    <div 
                      key={i} 
                      className={`entry-indicator entry-${entry.entry_type}`}
                      title={entry.title}
                    >
                      <span className="indicator-icon">
                        {entry.icon || (entry.entry_type === 'rehearsal_notes' ? '📝' : 
                         entry.entry_type === 'performance' ? '🎭' : 
                         entry.entry_type === 'notice' ? '📢' : '📅')}
                      </span>
                      <span className="indicator-text">
                        {entry.entry_type === 'rehearsal_notes' ? 'Notes' : 
                         entry.entry_type === 'performance' ? 'Perf' : 
                         entry.entry_type === 'notice' ? 'Notice' : 
                         entry.title.substring(0, 6)}
                      </span>
                    </div>
                  ))}
                </div>
                
                {/* Entry titles and rehearsal info (for larger screens) */}
                <div className="day-entries">
                  {day.rehearsals.map((rehearsal, i) => {
                    const hasNotes = day.entries.some(entry => entry.entry_type === 'rehearsal_notes');
                    return (
                      <div 
                        key={`rehearsal-${i}`} 
                        className={`rehearsal-entry ${hasNotes ? 'has-notes' : ''}`}
                      >
                        Rehearsal {rehearsal.time} {hasNotes ? '📝' : ''}
                      </div>
                    );
                  })}
                  {day.entries.slice(0, 2).map((entry, i) => (
                    <div key={i} className={`entry-title entry-${entry.entry_type}`}>
                      {entry.title}
                    </div>
                  ))}
                  {day.entries.length > 2 && (
                    <div className="more-entries">
                      +{day.entries.length - 2} more
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {loading && (
          <div className="calendar-loading">
            Loading calendar...
          </div>
        )}
        
        {/* Dialogs */}
        {showEntryDialog && (
          <CalendarEntryDialog
            date={selectedDate}
            entry={editingEntry}
            onSave={handleEntrySaved}
            onCancel={() => setShowEntryDialog(false)}
          />
        )}
        
        {showViewDialog && selectedEntry && (
          <CalendarViewDialog
            entry={selectedEntry}
            onEdit={canManageCalendar ? handleEditEntry : null}
            onDelete={canManageCalendar ? handleDeleteEntry : null}
            onClose={() => setShowViewDialog(false)}
          />
        )}
        
        {showDayDialog && selectedDay && (
          <CalendarDayDialog
            date={selectedDay.dateStr}
            entries={selectedDay.entries}
            rehearsals={selectedDay.rehearsals}
            onSelectEntry={handleDayDialogSelection}
            onClose={() => setShowDayDialog(false)}
          />
        )}
      </div>
    </CollapsibleSection>
  );
};

export default Calendar;